import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Building2, ClipboardList, MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Column, DataTable, LoadingState, PageHeader } from '@/components/common';
import { DetailLayout, DetailList, RailCard, StatStrip } from '@/components/common/DetailPanels';
import { consignmentApi, type CallOff, type CallOffLine, type CallOffStatus } from '@/api/services';
import { formatDateTime } from '@/lib/utils';
import { cn } from '@/lib/utils';

/**
 * One call-off — what was drawn, from where, and what happened to it.
 *
 * The list can show two lines of a draw before it truncates, which is fine for
 * scanning and useless for the thing a call-off actually is: the moment title
 * passed, and therefore the document somebody bills against and comes back to
 * argue about. That makes it a record with its own address rather than a row.
 */

const STATUS_TONE: Record<CallOffStatus, string> = {
  REQUESTED: 'bg-status-warning-subtle text-status-warning-foreground',
  APPROVED: 'bg-status-info-subtle text-status-info-foreground',
  FULFILLED: 'bg-status-info-subtle text-status-info-foreground',
  COLLECTED: 'bg-status-success-subtle text-status-success-foreground',
  REJECTED: 'bg-status-destructive-subtle text-status-destructive-foreground',
  CANCELLED: 'bg-muted text-muted-foreground',
};

const STATUS_LABEL: Record<CallOffStatus, string> = {
  REQUESTED: 'Requested',
  APPROVED: 'Approved',
  FULFILLED: 'Title passed',
  COLLECTED: 'Collected',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

/** What each state means for the material, in the reader's terms. */
const STATUS_MEANING: Record<CallOffStatus, string> = {
  REQUESTED: 'Raised and waiting on a decision. Nothing has moved.',
  APPROVED: 'Cleared to draw. The stock is still ours until it is fulfilled.',
  FULFILLED: 'Title has passed. The material is the customer’s and still standing at the node.',
  COLLECTED: 'Title passed and the material has left our position.',
  REJECTED: 'Turned down. Nothing moved.',
  CANCELLED: 'Withdrawn before it was drawn.',
};

function money(value: number | null | undefined): string {
  if (value == null) return '—';
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
}

export default function CallOffDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [callOff, setCallOff] = useState<CallOff | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setCallOff(await consignmentApi.callOff(id));
    } catch {
      toast.error('Could not load that call-off');
      navigate('/consignment');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setActing(true);
    try {
      await fn();
      toast.success(label);
      await load();
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'That did not work';
      toast.error(message);
    } finally {
      setActing(false);
    }
  };

  if (loading) return <LoadingState />;
  if (!callOff) return null;

  const settled = callOff.status === 'FULFILLED' || callOff.status === 'COLLECTED';
  const pricedLines = callOff.lines.filter((l) => l.unitPrice != null);
  const total = pricedLines.reduce(
    (sum, l) => sum + (l.unitPrice as number) * l.quantityFulfilled,
    0
  );
  const unpriced = callOff.lines.length - pricedLines.length;

  const columns: Column<CallOffLine>[] = [
    {
      key: 'sku',
      header: 'Item',
      accessorFn: (l) => l.item.sku,
      linkTo: (l) => `/inventory/${l.itemId}`,
      render: (l) => (
        <div>
          <div className="font-medium">{l.item.sku}</div>
          <div className="text-xs text-muted-foreground">{l.item.name}</div>
        </div>
      ),
    },
    {
      key: 'quantityRequested',
      header: 'Requested',
      className: 'text-right tabular-nums',
      render: (l) => `${l.quantityRequested}${l.item.unit ? ` ${l.item.unit}` : ''}`,
    },
    {
      key: 'quantityFulfilled',
      header: 'Drawn',
      className: 'text-right tabular-nums',
      render: (l) =>
        settled ? (
          l.quantityFulfilled
        ) : (
          // Not yet zero — not yet anything. Printing 0 before a decision reads
          // as "none was drawn" rather than "nothing has happened".
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'unitPrice',
      header: 'Unit price',
      className: 'text-right tabular-nums',
      accessorFn: (l) => l.unitPrice ?? -1,
      render: (l) =>
        l.unitPrice == null ? (
          <span className="text-muted-foreground">{settled ? 'unpriced' : '—'}</span>
        ) : (
          money(l.unitPrice)
        ),
    },
    {
      key: 'lineTotal',
      header: 'Line total',
      className: 'text-right tabular-nums',
      accessorFn: (l) => (l.unitPrice ?? 0) * l.quantityFulfilled,
      render: (l) =>
        settled && l.unitPrice != null ? (
          <span className="font-medium">{money(l.unitPrice * l.quantityFulfilled)}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ];

  // Only what this state can actually do next. A disabled button for a
  // transition that will never apply is noise dressed as an affordance.
  const actions =
    callOff.status === 'REQUESTED' ? (
      <div className="flex gap-2">
        <Button
          variant="outline"
          disabled={acting}
          onClick={() =>
            act(`${callOff.reference} approved`, () => consignmentApi.approve(callOff.id))
          }
        >
          Approve
        </Button>
        <Button
          variant="ghost"
          disabled={acting}
          onClick={() =>
            act(`${callOff.reference} rejected`, () => consignmentApi.reject(callOff.id))
          }
        >
          Reject
        </Button>
      </div>
    ) : callOff.status === 'APPROVED' ? (
      <div className="flex gap-2">
        <Button
          disabled={acting}
          onClick={() =>
            act(`${callOff.reference} drawn and collected`, () =>
              consignmentApi.fulfil(callOff.id, false)
            )
          }
        >
          Draw &amp; collect
        </Button>
        <Button
          variant="outline"
          disabled={acting}
          onClick={() =>
            act(`${callOff.reference}: title passed, goods retained`, () =>
              consignmentApi.fulfil(callOff.id, true)
            )
          }
        >
          Title only
        </Button>
      </div>
    ) : callOff.status === 'FULFILLED' ? (
      <Button
        variant="outline"
        disabled={acting}
        onClick={() =>
          act(`${callOff.reference} collected`, () => consignmentApi.collect(callOff.id))
        }
      >
        Record collection
      </Button>
    ) : null;

  return (
    <motion.main className="space-y-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <PageHeader
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Consignment', href: '/consignment' },
          { label: callOff.reference },
        ]}
        title={callOff.reference}
        description={`${callOff.customerCompany.name} drawing against stock held at ${callOff.location.name}.`}
        titleAdornment={
          <Badge className={cn('font-normal', STATUS_TONE[callOff.status])}>
            {STATUS_LABEL[callOff.status]}
          </Badge>
        }
        actions={actions}
      />

      <DetailLayout
        main={
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Lines</CardTitle>
                <CardDescription>{STATUS_MEANING[callOff.status]}</CardDescription>
              </CardHeader>
              <CardContent>
                <DataTable<CallOffLine>
                  pagination={false}
                  data={callOff.lines}
                  columns={columns}
                  emptyTitle="No lines"
                  emptyDescription="This call-off has no items on it, which should not be possible. Raise it again."
                />

                {settled && (
                  <div className="mt-4 border-t pt-3">
                    <StatStrip
                      caption="What passed"
                      stats={[
                        {
                          label: 'Units drawn',
                          value: callOff.lines.reduce((s, l) => s + l.quantityFulfilled, 0),
                        },
                        {
                          label: 'Value',
                          value: pricedLines.length ? money(total) : 'unpriced',
                        },
                        {
                          // Keyed on what happened, not on how it was fulfilled.
                          // `retainedAtNode` stays true after a later collection,
                          // so reading it alone reported a collected call-off as
                          // still standing at the node.
                          label: 'Left our position',
                          value: callOff.collectedAt
                            ? formatDateTime(callOff.collectedAt)
                            : 'not yet — still at the node',
                        },
                      ]}
                    />
                    {unpriced > 0 && (
                      // The total is over some of the lines, and saying so beside
                      // it is the difference between a figure and a misleading one.
                      <p className="mt-2 text-xs text-status-warning-foreground">
                        {unpriced} of {callOff.lines.length} lines carried no unit cost when title
                        passed, so this total is not the whole of it.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {callOff.retainedAtNode && callOff.status === 'FULFILLED' && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Still standing at the node</CardTitle>
                  <CardDescription>
                    Title passed on {formatDateTime(callOff.fulfilledAt as string)}, and the
                    material has not moved. It is the customer&rsquo;s, it is off our position, and
                    it is still physically at {callOff.location.name} — so it shows there as theirs
                    rather than as stock we can offer.
                  </CardDescription>
                </CardHeader>
              </Card>
            )}
          </div>
        }
        rail={
          <div className="space-y-4">
            <RailCard title="Customer" icon={<Building2 className="h-4 w-4" />}>
              <DetailList
                items={[
                  { label: 'Company', value: callOff.customerCompany.name },
                  {
                    label: 'Node',
                    value: (
                      <Link
                        to={`/locations/${callOff.location.id}`}
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {callOff.location.code}
                      </Link>
                    ),
                  },
                  { label: 'Site', value: callOff.location.name },
                ]}
              />
            </RailCard>

            <RailCard title="Trail" icon={<ClipboardList className="h-4 w-4" />}>
              <DetailList
                items={[
                  {
                    label: 'Raised',
                    value: `${formatDateTime(callOff.requestedAt)}${
                      callOff.requestedBy ? ` · ${callOff.requestedBy.fullName}` : ''
                    }`,
                  },
                  {
                    label: callOff.status === 'REJECTED' ? 'Rejected' : 'Approved',
                    value: `${formatDateTime(callOff.decidedAt as string)}${
                      callOff.decidedBy ? ` · ${callOff.decidedBy.fullName}` : ''
                    }`,
                    when: !!callOff.decidedAt,
                  },
                  {
                    label: 'Title passed',
                    value: formatDateTime(callOff.fulfilledAt as string),
                    when: !!callOff.fulfilledAt,
                    tone: 'info',
                  },
                  {
                    label: 'Collected',
                    value: formatDateTime(callOff.collectedAt as string),
                    when: !!callOff.collectedAt,
                    tone: 'success',
                  },
                  {
                    label: 'Decision note',
                    value: callOff.decisionNote,
                    when: !!callOff.decisionNote,
                  },
                  { label: 'Notes', value: callOff.notes, when: !!callOff.notes },
                ]}
              />
            </RailCard>

            <RailCard title="Where it sits" icon={<MapPin className="h-4 w-4" />}>
              <p className="text-sm text-muted-foreground">
                A call-off is the point at which title passes to the customer. It is not a purchase
                order, because nothing is being bought in, and not a consumption record, which
                covers internal usage.
              </p>
              <Link
                to="/consignment"
                className="mt-2 inline-block text-sm text-primary underline-offset-4 hover:underline"
              >
                All consignment nodes
              </Link>
            </RailCard>
          </div>
        }
      />
    </motion.main>
  );
}
